/* ==========================================================================
   FXT Racing theme: theme.js
   Global behavior: sticky header state, mobile menu, predictive search,
   cart drawer (Section Rendering API), quick add, collection filters,
   scroll reveal. Product page behavior lives in product.js.
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.themeRoutes || {};
  var strings = window.themeStrings || {};
  var root = document.documentElement;

  /* ---------- Helpers ---------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function announce(message) {
    var region = qs('[data-live-region]');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 40);
  }

  var toastEl, toastTimer;
  function toast(message) {
    if (!message) return;
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    // Force a reflow so the transition runs on repeated toasts.
    void toastEl.offsetWidth;
    toastEl.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () { toastEl.classList.remove('is-visible'); }, 4200);
  }

  /* ---------- Scroll lock and focus handling for overlays ---------- */
  var lockCount = 0;
  function lockScroll() {
    lockCount += 1;
    root.classList.add('scroll-locked');
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) root.classList.remove('scroll-locked');
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function focusables(container) {
    return qsa(FOCUSABLE, container).filter(function (el) { return el.getClientRects().length > 0; });
  }

  function openLayer(el, focusTarget, onOpen, onClose) {
    if (!el || el.classList.contains('is-open')) return;
    el._returnFocus = document.activeElement;
    el._onClose = onClose || null;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lockScroll();
    el._onKeydown = function (e) {
      if (e.key === 'Escape') {
        closeLayer(el);
        return;
      }
      if (e.key !== 'Tab') return;
      var items = focusables(el);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', el._onKeydown);
    if (onOpen) onOpen();
    window.setTimeout(function () {
      var target = focusTarget || focusables(el)[0];
      if (target && target.focus) target.focus();
    }, 40);
  }

  function closeLayer(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlockScroll();
    if (el._onKeydown) document.removeEventListener('keydown', el._onKeydown);
    if (el._onClose) el._onClose();
    if (el._returnFocus && el._returnFocus.focus) el._returnFocus.focus();
  }

  /* ---------- Header: solid background after scrolling ---------- */
  var header = qs('[data-site-header]');
  if (header) {
    var ticking = false;
    var updateHeader = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    };
    updateHeader();
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      }
    }, { passive: true });
  }

  /* ---------- Mobile menu ---------- */
  var drawer = qs('[data-mobile-drawer]');
  var drawerToggle = qs('[data-mobile-nav-toggle]');
  if (drawer && drawerToggle) {
    drawerToggle.addEventListener('click', function () {
      openLayer(
        drawer,
        qs('[data-drawer-close]:not(.mobile-drawer__overlay)', drawer),
        function () { drawerToggle.setAttribute('aria-expanded', 'true'); },
        function () { drawerToggle.setAttribute('aria-expanded', 'false'); }
      );
    });
    qsa('[data-drawer-close]', drawer).forEach(function (el) {
      el.addEventListener('click', function () { closeLayer(drawer); });
    });
    // Close the menu when the viewport grows to desktop size.
    window.matchMedia('(min-width: 990px)').addEventListener('change', function (mq) {
      if (mq.matches) closeLayer(drawer);
    });
  }

  /* ---------- Search overlay and predictive search ---------- */
  var searchOverlay = qs('[data-search-overlay]');
  if (searchOverlay) {
    var searchInput = qs('[data-search-input]', searchOverlay);
    var resultsBox = qs('[data-predictive-results]', searchOverlay);
    var debounceTimer;
    var controller = null;

    qsa('[data-search-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () { openLayer(searchOverlay, searchInput); });
    });
    qsa('[data-search-close]', searchOverlay).forEach(function (btn) {
      btn.addEventListener('click', function () { closeLayer(searchOverlay); });
    });
    searchOverlay.addEventListener('click', function (e) {
      if (e.target === searchOverlay || e.target.classList.contains('search-overlay__panel')) closeLayer(searchOverlay);
    });

    var runPredictive = function (term) {
      if (controller) controller.abort();
      controller = typeof AbortController === 'function' ? new AbortController() : null;
      var url = (routes.predictiveSearch || '/search/suggest') +
        '?q=' + encodeURIComponent(term) +
        '&section_id=predictive-search' +
        '&resources[type]=product,collection,query&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';
      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.ok ? r.text() : Promise.reject(r); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var content = qs('[data-predictive-content]', doc);
          resultsBox.innerHTML = content ? content.outerHTML : '';
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          resultsBox.innerHTML = '';
        });
    };

    if (searchInput && resultsBox) {
      searchInput.addEventListener('input', function () {
        window.clearTimeout(debounceTimer);
        var term = searchInput.value.trim();
        if (term.length < 2) {
          if (controller) controller.abort();
          resultsBox.innerHTML = '';
          return;
        }
        debounceTimer = window.setTimeout(function () { runPredictive(term); }, 220);
      });

      // Arrow keys move between the input and the results.
      searchOverlay.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        var items = [searchInput].concat(qsa('a', resultsBox));
        var index = items.indexOf(document.activeElement);
        if (index < 0) return;
        e.preventDefault();
        var next = e.key === 'ArrowDown' ? Math.min(items.length - 1, index + 1) : Math.max(0, index - 1);
        items[next].focus();
      });
    }
  }

  /* ---------- Scroll reveal ---------- */
  var revealObserver = null;
  if (root.classList.contains('animations-enabled') &&
      'IntersectionObserver' in window &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });
  }
  function observeReveal(scope) {
    qsa('.reveal:not(.revealed)', scope).forEach(function (el) {
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('revealed');
    });
  }
  observeReveal(document);

  /* ---------- Quantity steppers (product page and cart page) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-qty-change]');
    if (!btn) return;
    var wrap = btn.closest('.qty-input');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var step = parseInt(btn.getAttribute('data-qty-change'), 10);
    var min = input.hasAttribute('min') ? parseInt(input.getAttribute('min'), 10) : 0;
    var value = parseInt(input.value, 10);
    if (isNaN(value)) value = min;
    input.value = Math.max(min, value + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Cart (drawer, quick add, API helpers) ---------- */
  var cartDrawer = qs('[data-cart-drawer]');
  var cartInner = cartDrawer ? qs('[data-cart-drawer-inner]', cartDrawer) : null;

  function updateCartCount(count) {
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count > 0 ? count : '';
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      if (count > 0) el.classList.add('is-bumped');
    });
  }

  function focusKey(el) {
    if (!el || !cartInner || !cartInner.contains(el)) return null;
    var parts = ['data-cart-step', 'data-cart-remove', 'data-cart-input'];
    for (var i = 0; i < parts.length; i += 1) {
      if (el.hasAttribute(parts[i])) {
        var key = '[' + parts[i] + (el.getAttribute(parts[i]) ? '="' + el.getAttribute(parts[i]) + '"' : '') + ']';
        if (el.hasAttribute('data-line')) key += '[data-line="' + el.getAttribute('data-line') + '"]';
        return key;
      }
    }
    return null;
  }

  function renderCartHtml(html, keepFocusKey) {
    if (!cartInner) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = qs('[data-cart-drawer-inner]', doc);
    if (!next) return;
    cartInner.innerHTML = next.innerHTML;
    var count = parseInt(next.getAttribute('data-item-count'), 10) || 0;
    cartInner.setAttribute('data-item-count', count);
    updateCartCount(count);
    if (keepFocusKey && cartDrawer.classList.contains('is-open')) {
      var again = qs(keepFocusKey, cartInner);
      var fallback = qs('.cart-drawer__title', cartInner);
      if (again) again.focus();
      else if (fallback) {
        fallback.setAttribute('tabindex', '-1');
        fallback.focus();
      }
    }
  }

  function parseResponse(response) {
    return response.json().then(function (data) {
      if (!response.ok || (data && data.status && Number(data.status) >= 400)) {
        var err = new Error((data && (data.description || data.message)) || strings.error || 'Error');
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  function refreshCart() {
    if (!cartInner) return Promise.resolve();
    return fetch((routes.cart || '/cart') + '?section_id=cart-drawer')
      .then(function (r) { return r.text(); })
      .then(function (html) { renderCartHtml(html); });
  }

  function cartAdd(form) {
    var data = new FormData(form);
    data.append('sections', 'cart-drawer');
    data.append('sections_url', window.location.pathname);
    return fetch((routes.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: data
    })
      .then(parseResponse)
      .then(function (result) {
        if (result.sections && result.sections['cart-drawer']) {
          renderCartHtml(result.sections['cart-drawer']);
          return result;
        }
        return refreshCart().then(function () { return result; });
      });
  }

  var changing = false;
  function cartChange(line, quantity, focusAfter) {
    if (changing) return Promise.resolve();
    changing = true;
    if (cartDrawer) cartDrawer.classList.add('is-loading');
    return fetch((routes.cartChange || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: 'cart-drawer',
        sections_url: window.location.pathname
      })
    })
      .then(parseResponse)
      .then(function (result) {
        if (result.sections && result.sections['cart-drawer']) {
          renderCartHtml(result.sections['cart-drawer'], focusAfter);
        }
        var item = result.items && result.items[line - 1];
        if (quantity > 0 && item && item.quantity < quantity) {
          var errorEl = qs('[data-cart-error]', cartInner || document);
          if (errorEl) {
            errorEl.textContent = strings.quantityLimit || strings.error;
            errorEl.hidden = false;
          }
          announce(strings.quantityLimit);
        }
        return result;
      })
      .catch(function (err) {
        var errorEl = qs('[data-cart-error]', cartInner || document);
        if (errorEl) {
          errorEl.textContent = err.message || strings.error;
          errorEl.hidden = false;
        } else {
          toast(err.message || strings.error);
        }
      })
      .then(function (result) {
        changing = false;
        if (cartDrawer) cartDrawer.classList.remove('is-loading');
        return result;
      });
  }

  function openCart() {
    if (!cartDrawer) return;
    openLayer(cartDrawer, qs('[data-cart-close]:not(.cart-drawer__overlay)', cartDrawer));
  }
  function closeCart() { closeLayer(cartDrawer); }

  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-cart-toggle]');
    if (toggle && cartDrawer && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      openCart();
      return;
    }
    if (e.target.closest('[data-cart-close]')) {
      closeCart();
      return;
    }
    var step = e.target.closest('[data-cart-step]');
    if (step) {
      var line = parseInt(step.getAttribute('data-line'), 10);
      var input = qs('[data-cart-input][data-line="' + line + '"]', cartInner);
      var current = input ? parseInt(input.value, 10) || 0 : 0;
      cartChange(line, Math.max(0, current + parseInt(step.getAttribute('data-cart-step'), 10)), focusKey(step));
      return;
    }
    var remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      cartChange(parseInt(remove.getAttribute('data-line'), 10), 0, null);
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-input]');
    if (!input) return;
    cartChange(
      parseInt(input.getAttribute('data-line'), 10),
      Math.max(0, parseInt(input.value, 10) || 0),
      focusKey(input)
    );
  });

  /* Quick add from product cards */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.hasAttribute || !form.hasAttribute('data-quick-add')) return;
    e.preventDefault();
    var button = qs('button[type="submit"]', form);
    if (button) button.classList.add('is-loading');
    cartAdd(form)
      .then(function () { openCart(); })
      .catch(function (err) { toast(err.message || strings.error); })
      .then(function () { if (button) button.classList.remove('is-loading'); });
  });

  /* Refresh stale cart state when the page is restored from the back/forward cache */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshCart();
  });

  /* ---------- Cart page: quantity and remove ---------- */
  var cartForm = qs('[data-cart-form]');
  if (cartForm) {
    var changeLine = function (line, quantity) {
      cartForm.classList.add('is-loading');
      fetch((routes.cartChange || '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(parseResponse)
        .then(function () { window.location.reload(); })
        .catch(function (err) {
          cartForm.classList.remove('is-loading');
          toast(err.message || strings.error);
        });
    };

    cartForm.addEventListener('change', function (e) {
      var input = e.target.closest('[data-line-quantity]');
      if (!input) return;
      changeLine(parseInt(input.getAttribute('data-line-quantity'), 10), Math.max(0, parseInt(input.value, 10) || 0));
    });

    cartForm.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-line-remove]');
      if (!remove) return;
      e.preventDefault();
      changeLine(parseInt(remove.getAttribute('data-line-remove'), 10), 0);
    });
  }

  /* ---------- Collection page: sort and filters ---------- */
  var filterToggle = qs('[data-filter-toggle]');
  var filterPanel = qs('[data-filters-panel]');
  if (filterToggle && filterPanel) {
    filterToggle.addEventListener('click', function () {
      var open = filterPanel.classList.toggle('is-open');
      filterToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  var sortSelect = qs('[data-sort-select]');
  if (sortSelect && sortSelect.form) {
    sortSelect.addEventListener('change', function () { sortSelect.form.submit(); });
  }

  var filterForm = qs('[data-filter-form]');
  if (filterForm) {
    var desktop = window.matchMedia('(min-width: 990px)');
    var numberInputs = qsa('input[type="number"]', filterForm);

    // On desktop, ticking a filter applies it right away. On small screens the
    // shopper picks several and taps Apply.
    filterForm.addEventListener('change', function (e) {
      if (e.target.matches('input[type="checkbox"]') && desktop.matches) {
        if (filterForm.requestSubmit) filterForm.requestSubmit();
        else filterForm.submit();
      }
    });

    // Keep empty price boxes out of the URL.
    filterForm.addEventListener('submit', function () {
      numberInputs.forEach(function (input) { if (input.value === '') input.disabled = true; });
    });
    window.addEventListener('pageshow', function () {
      numberInputs.forEach(function (input) { input.disabled = false; });
    });
  }

  /* ---------- Public API for product.js and other scripts ---------- */
  window.FXT = {
    announce: announce,
    toast: toast,
    observeReveal: observeReveal,
    openLayer: openLayer,
    closeLayer: closeLayer,
    cart: {
      add: cartAdd,
      change: cartChange,
      refresh: refreshCart,
      open: openCart,
      close: closeCart
    }
  };
})();
